import { css, cx } from "@emotion/css";
import Badge from "@mui/material/Badge";
import MuiList from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import MuiMenuItem from "@mui/material/MenuItem";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { DeleteAccountLosses } from "src/components/Modals/DeleteModals/DeleteAccountLosses";
import { useTawk } from "src/components/TawkMessenger/TawkMessengerProvider";
import { isLekEn } from "src/helpers/services";
import { getAppConfig } from "src/hooks/useAppConfig";
import { getUserInfo, useLogout, useRemoveUser } from "src/hooks/useUser";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/account.svg";
import HelpIcon from "src/images/Icons/newIcons/userProfile/help.svg";
import LogoutIcon from "src/images/Icons/newIcons/userProfile/logout.svg";
import ReloadIcon from "src/images/Icons/newIcons/userProfile/reload.svg";
import ReportIcon from "src/images/Icons/newIcons/userProfile/reportError.svg";
import SurveyIcon from "src/images/Icons/newIcons/userProfile/survey.svg";

interface ProfileAction {
	Icon: ReactNode;
	title: string;
	divider?: boolean;
	textClassName?: string;
	onClick?: () => void;
	href?: string;
	to?: string;
	testId?: string;
	badge?: number;
}

export const ProfileActionsList = () => {
	const { openModal } = useModals();
	const { openChat, chatUrl } = useTawk();
	const appConfig = getAppConfig();
	const { mutateAsync: logout } = useLogout();
	const { mutateAsync: removeUser } = useRemoveUser() as unknown as {
		mutateAsync: () => Promise<void>;
	};
	const { t } = useTranslation();

	const items: (ProfileAction | false)[] = [
		{
			Icon: <ProfileIcon className="icon-normal" />,
			title: t("profile.tabs.myProfile"),
			to: routing.paths.userProfile,
		},
		{
			Icon: <ReloadIcon className="icon-normal" />,
			title: t("profile.tabs.refresh"),
			onClick: () => location.reload(),
		},
		!isLekEn() && {
			Icon: <HelpIcon className="icon-normal" />,
			title: t("profile.tabs.blog"),
			href: appConfig.blogUrl,
		},
		appConfig.surveyUrl && {
			Icon: <SurveyIcon className="icon-normal" />,
			title: t("modals.settings.survey"),
			href: appConfig.surveyUrl,
		},
		(!!chatUrl || !!openChat) && {
			Icon: <ReportIcon className="icon-normal" />,
			title: t("modals.reportTechnicalIssues.buttonText"),
			divider: true,
			onClick: () => {
				if (chatUrl) {
					window.location.href = chatUrl;
				} else {
					openChat?.();
				}
			},
		},
		{
			Icon: <DeleteIcon className="icon-normal icon-error" />,
			title: t("profile.tabs.removeAccount"),
			textClassName: "text-error",
			onClick: () => {
				const user = getUserInfo();
				openModal(ModalsNames.DeleteItemModal, {
					title: t("modals.deleteItem.account.title", "Usunąć konto?"),
					description: (
						<Trans
							i18nKey="modals.deleteItem.account.description"
							defaults="Konto <0>{{email}}</0> zostanie trwale usunięte. Tej operacji nie można cofnąć."
							values={{ email: user.email }}
							components={[<strong key="email" className="text-black" />]}
						/>
					),
					children: <DeleteAccountLosses user={user} />,
					confirmCheckboxLabel: t(
						"modals.deleteItem.account.confirmCheckbox",
						"Rozumiem, że moje dane zostaną trwale usunięte i nie będzie można ich odzyskać.",
					),
					confirmText: t("modals.deleteItem.account.confirm", "Usuń konto"),
					onDelete: () => removeUser(),
				});
			},
		},
		{
			Icon: <LogoutIcon className="icon-normal" />,
			title: t("profile.tabs.logOut"),
			testId: "logout-button",
			onClick: async () => {
				await logout();
			},
		},
	];

	const filteredItems = items.filter(Boolean) as ProfileAction[];

	return (
		<MuiList className="p-12">
			{filteredItems.map(
				({
					Icon,
					title,
					divider = false,
					textClassName = "",
					onClick,
					href,
					to,
					testId,
					badge = 0,
				}) => (
					<MuiMenuItem
						className={cx("menu-item p-12")}
						key={title}
						divider={divider}
						onClick={onClick}
						{...(testId && { "data-test-id": testId })}
						{...(href && {
							component: "a" as const,
							target: "_blank",
							rel: "noopener noreferrer",
							href,
						})}
						{...(to && { component: Link, to })}
					>
						<ListItemIcon
							className={css`
              min-width: 0 !important;
            `}
						>
							<Badge badgeContent={badge} color="secondary">
								{Icon}
							</Badge>
						</ListItemIcon>
						<div className="flex flex-col">
							<span className={cx("size-14-light", textClassName)}>
								{title}
							</span>
						</div>
					</MuiMenuItem>
				),
			)}
		</MuiList>
	);
};
